import { useEffect, useRef, useState } from 'react'

export type CtxTarget = { x: number; y: number; path: string; name: string; isDir: boolean }

/**
 * PER-ENTRY CONTEXT MENU — owner, 2026-08-25: 右鍵點擊或是長按…複製他們的路徑.
 * One menu, two doors, every surface (通盤考慮): right-click is the desktop
 * long-press, and on touch a 500ms pointer hold opens the same menu — iOS
 * fires no contextmenu event for arbitrary elements, so the hold is the
 * mechanism there, not a nicety. A fired flag (`ctxFired`) swallows the
 * click that follows a hold, so a long-press never ALSO opens the file
 * behind it. A hold that wanders more than a few px is a
 * scroll, not a press — cancelled, or the list could never be flicked.
 *
 * Split out of FileBrowser.tsx in M6 (拆面板, 2026-09-03), moved verbatim.
 */
export function useCtxMenu(path: string | null) {
  const [ctx, setCtx] = useState<CtxTarget | null>(null)
  const ctxHold = useRef<number | null>(null)
  const ctxFired = useRef(false)
  const ctxStart = useRef<{ x: number; y: number }>({ x: 0, y: 0 })
  const cancelHold = () => {
    if (ctxHold.current) window.clearTimeout(ctxHold.current)
    ctxHold.current = null
  }
  /*
   * The clamp's budget, split so it can grow with the menu.
   *
   * These are not tight measurements and are not meant to be: the CSS draws a name row
   * and ~30px items, so the old 132 already carried ~39px of slack for two items. The
   * split keeps exactly that generosity (2 items still clamps at 132) and lets a third
   * item ask for its own room instead of hanging off the bottom of a phone.
   */
  const CTX_CHROME = 60
  const CTX_ITEM = 36

  const openCtx = (x: number, y: number, entryPath: string, name: string, isDir: boolean) => {
    // Clamped so the menu never opens half off-screen — a phone thumb presses
    // near edges more often than a mouse does.
    //
    // DERIVED from how many items this entry actually shows, not a constant: the
    // clamp was 132 for a two-item menu, and adding Rename in 2026-09 would have left
    // a three-item menu hanging its last row off the bottom of a phone — a menu item
    // you can see but not reach. A folder shows Copy path + Rename; a file also shows
    // Download file.
    const items = isDir ? 2 : 3
    setCtx({
      x: Math.min(x, window.innerWidth - 200),
      y: Math.min(y, window.innerHeight - (CTX_CHROME + items * CTX_ITEM)),
      path: entryPath,
      name,
      isDir,
    })
  }
  // The menu outlives no folder change: a path copied must be the path on
  // screen. Keyed on `path` (the navigation state) rather than the later-derived
  // `cwd`: every user navigation sets `path`, and the one cwd change without it —
  // the first server resolution — happens before any menu can exist.
  useEffect(() => {
    setCtx(null)
  }, [path])
  useEffect(() => {
    if (!ctx) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setCtx(null)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [ctx])

  return { ctx, setCtx, ctxHold, ctxFired, ctxStart, cancelHold, openCtx }
}
